import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import { autorun } from 'mobx';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import CarouselSlide from '../CarouselSlide';
import CarouselWrapper from '../CarouselWrapper';
import Loading from '../Loading/Loading';

const Carousel = ({ hotTopics, popularQuestions, content, jobSearch, sourceLanguage }) => {
  const { i18n } = useTranslation();
  const {
    fontFamily,
    button,
    window: { colors }
  } = useConfig();
  const { bot, chatWindow } = useStores();
  const [loaded, setLoaded] = React.useState(false);
  const [fullData, setFullData] = React.useState([]);
  const [isVisibleButtons, setVisibleButtons] = React.useState(false);
  const [isReady, setReady] = React.useState(false);
  const [buttonTranslation, setButtonTranslation] = React.useState('');

  const filteredTopFrequentQuestions = receivedArray => {
    const filteredArray = [];
    receivedArray.forEach(item => {
      filteredArray.push({
        text: item.MessageText,
        mesId: item.MesID
      });
    });

    return filteredArray;
  };

  const renderData = () => {
    const data = [];

    if (jobSearch) {
      forEach(content, (item, index) => {
        data.push({
          name: item.Title,
          jobSearchContent: {
            industry: item.Industry,
            seniority: item.Seniority,
            salary: item.Salary,
            link: item.JobDetailsUrl
          },
          isTopFrequentQuestions: false,
          position: index + 1
        });
        return data;
      });
    } else {
      forEach(content, item => {
        if (item.name === 'hotTopics') {
          if (!hotTopics.length) return null;

          data.push({
            name: item.name,
            imageLink: item.imageLink,
            list: [...filteredTopFrequentQuestions(hotTopics)],
            isTopFrequentQuestions: true,
            position: item.position
          });
          return data;
        }

        if (item.name === 'popularQuestions') {
          if (!popularQuestions.length) return null;

          data.push({
            name: item.name,
            imageLink: item.imageLink,
            list: [...filteredTopFrequentQuestions(popularQuestions)],
            isTopFrequentQuestions: true,
            position: item.position
          });
          return data;
        }

        data.push(item);
        return data;
      });
    }

    data.sort((a, b) => a.position - b.position);

    return data;
  };

  const data = renderData();

  // receiving intent data
  React.useEffect(() => {
    if (!fullData.length && !loaded && !isReady) {
      setReady(true);
      bot.getMqaCarouselData(data, sourceLanguage || i18n.language).then(response => {
        if (response.length) {
          const newData = response.sort((a, b) => a.position - b.position);
          setFullData(newData);
          setVisibleButtons(newData.length * 228 > chatWindow.dimensions.width);
        }
        setLoaded(true);
      });
    }
  }, [
    bot,
    chatWindow.dimensions.width,
    data,
    fullData.length,
    i18n.language,
    isReady,
    loaded,
    sourceLanguage
  ]);

  // show or hide navigation buttons, depends on the chat window width
  React.useEffect(() => {
    autorun(() => {
      if (fullData.length && loaded) {
        setVisibleButtons(fullData.length * 228 > chatWindow.dimensions.width);
      }
    });
  }, [chatWindow.dimensions.width, fullData.length, loaded]);

  // detect translation for button
  React.useEffect(() => {
    const languagesKeys = Object.keys(i18n.store.data);
    const isCurrentLanguage = includes(languagesKeys, sourceLanguage);

    if (sourceLanguage && sourceLanguage !== i18n.language && !isCurrentLanguage) {
      i18n.loadLanguages(sourceLanguage);
      i18n.store.on('added', () => {
        setButtonTranslation(i18n.getResource(sourceLanguage, 'translation', 'viewMore'));
      });
    } else if (!sourceLanguage) {
      setButtonTranslation(i18n.getResource(i18n.language, 'translation', 'viewMore'));
    } else {
      setButtonTranslation(i18n.getResource(sourceLanguage, 'translation', 'viewMore'));
    }
  }, [i18n, sourceLanguage]);

  return (
    <Root fontFamily={fontFamily} mainColor={button.color}>
      {!loaded || !buttonTranslation ? (
        <LoaderWrapper>
          <Loading text="" color={colors.accent} size={14} />
        </LoaderWrapper>
      ) : (
        <CarouselWrapper isVisibleButtons={isVisibleButtons}>
          {fullData.map((item, index) => {
            return (
              <CarouselSlide
                key={index}
                index={index + 1}
                data={item}
                language={sourceLanguage}
                buttonTranslation={buttonTranslation}
              />
            );
          })}
        </CarouselWrapper>
      )}
    </Root>
  );
};

Carousel.propTypes = {
  hotTopics: PropTypes.arrayOf(PropTypes.shape({})),
  popularQuestions: PropTypes.arrayOf(PropTypes.shape({})),
  content: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  jobSearch: PropTypes.bool,
  sourceLanguage: PropTypes.string
};

Carousel.defaultProps = {
  hotTopics: [],
  popularQuestions: [],
  jobSearch: false,
  sourceLanguage: ''
};

const Root = styled.div`
  width: 100%;
  font-family: ${props => props.fontFamily};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 228px;
`;

export default Carousel;
